import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { FileText, Download, Eye, Loader2, Shield, Clock, FolderOpen, Receipt, CreditCard, PlayCircle, ExternalLink, File, Image, Video, FileArchive, FileCheck } from 'lucide-react';
import { viewDocument, downloadDocument } from '../services/downloadService';
import { formatDate } from './mesDocumentsHelpers';

export default function MesDocumentsSections({
  souscriptionsEnCours, recusPaiements, documentsGed, documentsAdmin, categories,
  activeFilter, downloadingId, handleDownloadRecu, handleView, handleDownload,
}) {
  return (
    <>
      {/* Section Souscriptions en cours (30 jours) */}
      {souscriptionsEnCours.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <PlayCircle className="h-5 w-5" />
              Simulations en cours ({souscriptionsEnCours.length})
            </CardTitle>
            <CardDescription>
              Reprendre vos simulations non finalisées (disponibles 30 jours)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {souscriptionsEnCours.map((sub) => (
              <div
                key={sub.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{sub.nom}</p>
                    {sub.jours_restants !== undefined && (
                      <Badge
                        variant="outline"
                        className={sub.jours_restants <= 7 ? 'border-red-300 text-red-600' : 'border-amber-300 text-amber-600'}
                      >
                        <Clock className="h-3 w-3 mr-1" />
                        {sub.jours_restants}j restants
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{sub.description}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span>Étape {sub.etape_actuelle || 1}/5</span>
                    <span>•</span>
                    <span>Créé le {formatDate(sub.date_creation)}</span>
                    {sub.montant_ttc && <span>• {sub.montant_ttc}€ Montant Total</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {sub.can_resume !== false && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500 text-amber-700 hover:bg-amber-100"
                      onClick={() => {
                        window.location.href = `/cooper/souscription-annuelle?resume=${sub.souscription_id}`;
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Reprendre
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Section Factures / Reçus ESS */}
      {recusPaiements.length > 0 && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-green-800">
              <Receipt className="h-5 w-5" />
              Factures / Reçus ESS ({recusPaiements.length})
            </CardTitle>
            <CardDescription>
              Vos reçus de paiement acquittés (12 derniers mois)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {recusPaiements.map((recu) => (
              <div
                key={recu.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    {recu.type === 'recu_paiement_ftpe' ? (
                      <Shield className="h-4 w-4 text-green-600" />
                    ) : (
                      <CreditCard className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{recu.nom}</p>
                    <p className="text-sm text-gray-500">{recu.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {recu.date_paiement ? `Payé le ${formatDate(recu.date_paiement)}` : formatDate(recu.date_creation)}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleDownloadRecu(recu)}
                  disabled={downloadingId === recu.id}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {downloadingId === recu.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </>
                  )}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Section Documents GED */}
      {documentsGed.length > 0 && (activeFilter === 'all' || activeFilter === 'ged') && (
        <Card className="border-indigo-200 bg-indigo-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-indigo-800">
              <FolderOpen className="h-5 w-5" />
              Documents GED ({documentsGed.length})
            </CardTitle>
            <CardDescription>
              Vos documents stockés dans la Gestion Électronique des Documents
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {documentsGed.map((doc) => {
              const getFileIcon = (mimeType) => {
                if (mimeType?.startsWith('image/')) return <Image className="h-5 w-5 text-purple-600" />;
                if (mimeType?.startsWith('video/')) return <Video className="h-5 w-5 text-red-600" />;
                if (mimeType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
                if (mimeType?.includes('zip') || mimeType?.includes('archive')) return <FileArchive className="h-5 w-5 text-yellow-600" />;
                return <File className="h-5 w-5 text-indigo-600" />;
              };

              const formatFileSize = (bytes) => {
                if (!bytes) return '';
                if (bytes < 1024) return `${bytes} B`;
                if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
              };

              const getCategorieLabel = (cat) => {
                const labels = {
                  'factures': 'Facture',
                  'dispositif_souscrit': 'Dispositif souscrit',
                  'dommages': 'Dommages',
                  'proposition': 'Proposition',
                  'conditions_generales': 'Conditions générales',
                  'devis': 'Devis',
                  'photos': 'Photo',
                  'rapport': 'Rapport',
                  'courrier': 'Courrier',
                  'piece_identite': 'Pièce d\'identité',
                  'contrat': 'Contrat',
                  'attestation': 'Attestation',
                  'autre': 'Autre'
                };
                return labels[cat] || cat || 'Document';
              };

              return (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-indigo-200"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      {getFileIcon(doc.mime_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 truncate">{doc.nom}</p>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            doc.status === 'valide' ? 'text-green-600 border-green-300' :
                            doc.status === 'rejete' ? 'text-red-600 border-red-300' :
                            'text-amber-600 border-amber-300'
                          }`}
                        >
                          {doc.status === 'valide' ? 'Validé' : doc.status === 'rejete' ? 'Rejeté' : 'En attente'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Badge variant="outline" className="text-indigo-600 border-indigo-300 text-xs py-0">
                          {getCategorieLabel(doc.categorie)}
                        </Badge>
                        {doc.file_size && <span>{formatFileSize(doc.file_size)}</span>}
                        {doc.date_creation && <span>• {formatDate(doc.date_creation)}</span>}
                      </div>
                      {doc.tags?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {doc.tags.slice(0, 3).map((tag, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs py-0 px-1">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {doc.url && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                          onClick={() => viewDocument(doc)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Voir
                        </Button>
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700"
                          onClick={() => downloadDocument(doc)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Télécharger
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Documents inserees par l'Admin */}
      {documentsAdmin.length > 0 && (
        <Card className="border-violet-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-violet-800" data-testid="admin-docs-section">
              <FileCheck className="h-5 w-5" />
              Documents inseres par l&apos;administration ({documentsAdmin.length})
            </CardTitle>
            <CardDescription>
              Attestations signees, devis et certificats inseres manuellement
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {documentsAdmin.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-200" data-testid={`admin-doc-${doc.id}`}>
                <div className="flex items-center gap-3 flex-1">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <FileText className="h-5 w-5 text-violet-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{doc.nom}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {doc.original_filename && <span>{doc.original_filename}</span>}
                      {doc.date_creation && <span>• {formatDate(doc.date_creation)}</span>}
                    </div>
                    {doc.uploaded_by_name && (
                      <span className="text-xs text-violet-600">Insere par {doc.uploaded_by_name}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-violet-300 text-violet-700" data-testid={`view-admin-doc-${doc.id}`}
                    onClick={() => handleView(doc)}>
                    <Eye className="h-4 w-4 mr-1" />Voir
                  </Button>
                  <Button size="sm" className="bg-violet-600 hover:bg-violet-700" data-testid={`dl-admin-doc-${doc.id}`}
                    onClick={() => handleDownload(doc)} disabled={downloadingId === doc.id}>
                    {downloadingId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1" />PDF</>}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Information sur l'attestation FTPE */}
      {categories.ftpe === 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">
                  Attestation FTPE Outremer
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  L&apos;attestation FTPE sera disponible ici une fois votre adhésion validée et payée.
                  Elle certifie votre appartenance à la Fédération des TPE d&apos;Outre-mer.
                </p>
                <Badge className="bg-blue-600 text-white font-semibold">Validité : 1 an</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
